import { Injectable } from "@angular/core";
import { Observable, map, race, timer } from "rxjs";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { Fce } from "./fce";
import { Location } from "@angular/common";
import { SharedService } from "../_shared/shared.service";
import { LoggedUserService } from "../_services/logged-user.service";

@Injectable()
export class AccessGuard {
  /** Upper bound (ms) on how long to wait for the post-login rights refresh
   *  before deciding anyway, so a stalled/failed refresh can't hang navigation. */
  private static readonly RIGHTS_WAIT_MS = 5000;

  constructor(
    protected shared: SharedService,
    private router: Router,
    private location: Location,
    private loggedUserService: LoggedUserService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    const requiresLogin = route.data.requiresLogin;
    if (requiresLogin && !this.shared.isLoggedIn()) {
      if (route.url)
        this.shared.setAny("last_wanted_url", this.location.path());
      if (!route.data.isMainPage) this.shared.warning("You are not logged in");
      this.router.navigate(["/login"]);
      return false;
    }

    if (
      this.shared.isLoggedIn() &&
      !this.shared.rightsAll &&
      this.decisionNeedsRights(route.data)
    ) {
      return this.waitForRights().pipe(map(() => this.evaluateAccess(route)));
    }

    return this.evaluateAccess(route);
  }

  /**
   * Synchronous access decision, evaluated once rights are known. Extracted from
   * {@link canActivate} so it can run either immediately (rights already loaded —
   * normal in-app navigation) or after {@link waitForRights} resolves (cold load).
   */
  private evaluateAccess(route: ActivatedRouteSnapshot): boolean {
    const app = route.data.app;
    const rights = route.data.rights;
    const urights = this.shared.rightsAll;
    const group = route.data.group;

    if (this.loggedUserService.hasGroup("Admin")) return true;
    if (this.shared.isAdmin()) return true;

    const groupVerdict = this.checkGroupAccess(route.data);
    if (groupVerdict !== undefined) return groupVerdict;

    if (!(app || group)) return true;

    if (this.loggedUserService.hasModule(app)) return true;

    return this.controlRights(
      app,
      rights,
      urights,
      group,
      route.data.allowAdmin,
    );
  }

  /**
   * Does this route's access decision actually depend on rights/modules/groups
   * that load asynchronously? Rights-free routes (e.g. the dashboard landing) are
   * decided immediately so a cold load isn't needlessly delayed by the refresh.
   */
  private decisionNeedsRights(data: any): boolean {
    if (data.app || data.group) return true;
    switch (data.accessControlType) {
      case "blacklist":
        return (data.forbiddenGroups?.length ?? 0) > 0;
      case "whitelist":
        return (data.allowedGroups?.length ?? 0) > 0;
      case "whitelist-module":
        return (data.allowedModules?.length ?? 0) > 0;
      default:
        return false;
    }
  }

  /**
   * Emits once the post-login `refresh` response has populated rights, or after a
   * timeout fallback so a failed/stalled refresh can't leave navigation hanging on
   * a blank screen forever (it then re-evaluates with whatever has loaded).
   */
  private waitForRights(): Observable<unknown> {
    return race(
      this.shared.rightsAllFirstNotNull,
      timer(AccessGuard.RIGHTS_WAIT_MS),
    );
  }

  /**
   * Blacklist/whitelist verdict from `data.forbiddenGroups` / `data.allowedGroups`.
   * Returns `false`/`true` to short-circuit (bypassing the app/rights check below),
   * or `undefined` when the group rules don't decide anything either way.
   */
  private checkGroupAccess(data: any): boolean | undefined {
    const ugroups = this.shared.groupsAll || [];

    if (
      data.accessControlType === "blacklist" &&
      ugroups.some((g) => data.forbiddenGroups?.some((f) => f.group === g.name))
    ) {
      return false;
    }

    if (
      data.accessControlType === "whitelist" &&
      ugroups.some((g) => data.allowedGroups?.some((f) => f.group === g.group))
    ) {
      return true;
    }

    if (data.accessControlType === "whitelist-module") {
      if (
        data.allowedModules?.some((m: string) =>
          this.loggedUserService.hasModule(m),
        )
      ) {
        return true;
      } else return false;
    }

    return undefined;
  }

  /**
   * Silent, synchronous equivalent of {@link canActivate} for UI use (e.g. hiding a
   * `[routerLink]` the user couldn't navigate to) — never warns, never navigates, and
   * never waits for rights to load: if `rightsAll` isn't populated yet, it treats the
   * route as inaccessible until the caller re-checks (rights arriving is the caller's
   * job to react to, not this method's).
   */
  canAccessSilently(data: any): boolean {
    if (data.requiresLogin && !this.shared.isLoggedIn()) return false;

    const groupVerdict = this.checkGroupAccess(data);
    if (groupVerdict !== undefined) return groupVerdict;

    const app = data.app;
    const rights = data.rights;
    const group = data.group;
    if (!(app || group)) return true;

    if (this.loggedUserService.hasModule(app)) return true;

    if (this.loggedUserService.hasGroup("Admin")) return true;

    return false;
  }

  controlRights(
    app: string,
    rights: [string],
    urights: any,
    group: string = null,
    allowAdmin: boolean = false,
  ) {
    // Opt-in: Admin má přístup k routám označeným data.allowAdmin.
    if (this.loggedUserService.hasGroup("Admin")) return true;
    if (!this.loggedUserService.hasModule(app)) {
      this.shared.warning("You dont have access here!");
      this.router.navigate(["/login"]);
      return false;
    }

    if (Fce.empty(rights)) {
      return true;
    }

    // OR logic
    for (const r of rights) {
      if (urights[app][r]) return true;
    }

    // Direct group name logic
    if (urights[app].group && urights[app].group.includes(group)) return true;
    if (this.loggedUserService.hasModule(app)) return true;

    this.shared.warning("You dont have user rights!");
    this.router.navigate(["/login"]);
    return false;
  }
}
